import { useState } from "react";
import { t } from "@/i18n";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, ChevronLeft, Shield, Zap, Globe, FileText, MapPin, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { syncKeysOnLogin } from "@/utils/e2e";
import { isNativeIOS, appleNativeLogin } from "@/utils/appleAuth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Country list
const COUNTRIES = [
  { code: 'IT', name: 'Italia', flag: '🇮🇹' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'ES', name: 'España', flag: '🇪🇸' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Deutschland', flag: '🇩🇪' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'BR', name: 'Brasil', flag: '🇧🇷' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'MX', name: 'México', flag: '🇲🇽' },
  { code: 'CH', name: 'Schweiz', flag: '🇨🇭' },
  { code: 'AT', name: 'Österreich', flag: '🇦🇹' },
  { code: 'NL', name: 'Nederland', flag: '🇳🇱' },
  { code: 'BE', name: 'België', flag: '🇧🇪' },
  { code: 'PL', name: 'Polska', flag: '🇵🇱' },
  { code: 'OTHER', name: 'Altro / Other', flag: '🌍' },
];

// Terms of Service content
const TermsContent = () => (
  <div className="prose prose-sm max-w-none text-gray-600">
    <h3 className="text-lg font-semibold text-gray-900">Termini e Condizioni di Utilizzo</h3>
    <p className="text-sm">Ultimo aggiornamento: Gennaio 2026</p>
    
    <h4 className="font-semibold mt-4">1. Accettazione dei Termini</h4>
    <p>Utilizzando l'applicazione ADE, accetti di essere vincolato da questi Termini di Servizio. Se non accetti questi termini, non utilizzare l'app.</p>
    
    <h4 className="font-semibold mt-4">2. Descrizione del Servizio</h4>
    <p>ADE è un'applicazione di social networking basata sulla prossimità che permette agli utenti di connettersi con persone nelle vicinanze utilizzando la geolocalizzazione.</p>
    
    <h4 className="font-semibold mt-4">3. Account Utente</h4>
    <ul className="list-disc pl-5">
      <li>Devi avere almeno 18 anni per utilizzare ADE</li>
      <li>Sei responsabile della sicurezza del tuo account</li>
      <li>Le informazioni fornite devono essere accurate</li>
    </ul>
    
    <h4 className="font-semibold mt-4">4. Comportamento dell'Utente</h4>
    <p>Ti impegni a non:</p>
    <ul className="list-disc pl-5">
      <li>Molestare, minacciare o intimidire altri utenti</li>
      <li>Pubblicare contenuti offensivi, illegali o inappropriati</li>
      <li>Creare profili falsi o impersonare altri</li>
      <li>Utilizzare l'app per scopi illegali</li>
    </ul>
    
    <h4 className="font-semibold mt-4">5. Proprietà Intellettuale</h4>
    <p>Tutti i contenuti dell'app sono protetti da copyright. L'utente mantiene i diritti sui propri contenuti ma concede ad ADE una licenza per utilizzarli nel servizio.</p>
    
    <h4 className="font-semibold mt-4">6. Limitazione di Responsabilità</h4>
    <p>ADE non è responsabile per interazioni tra utenti o danni derivanti dall'uso dell'app.</p>
    
    <h4 className="font-semibold mt-4">7. Modifiche ai Termini</h4>
    <p>Ci riserviamo il diritto di modificare questi termini. Gli utenti saranno notificati delle modifiche significative.</p>
  </div>
);

// Privacy Policy content
const PrivacyContent = () => (
  <div className="prose prose-sm max-w-none text-gray-600">
    <h3 className="text-lg font-semibold text-gray-900">Informativa sulla Privacy (GDPR)</h3>
    <p className="text-sm">Ultimo aggiornamento: Gennaio 2026</p>
    
    <h4 className="font-semibold mt-4">1. Titolare del Trattamento</h4>
    <p>Il titolare del trattamento dei dati personali è ADE App.</p>
    
    <h4 className="font-semibold mt-4">2. Dati Raccolti</h4>
    <ul className="list-disc pl-5">
      <li><strong>Dati di registrazione:</strong> nome, email, foto profilo</li>
      <li><strong>Dati di geolocalizzazione:</strong> posizione GPS in tempo reale</li>
      <li><strong>Dati di utilizzo:</strong> interazioni, messaggi, connessioni</li>
      <li><strong>Dati del dispositivo:</strong> tipo di dispositivo, sistema operativo</li>
    </ul>
    
    <h4 className="font-semibold mt-4">3. Finalità del Trattamento</h4>
    <ul className="list-disc pl-5">
      <li>Fornire il servizio di social networking</li>
      <li>Permettere la rilevazione di utenti nelle vicinanze</li>
      <li>Migliorare l'esperienza utente</li>
      <li>Garantire la sicurezza del servizio</li>
    </ul>
    
    <h4 className="font-semibold mt-4">4. Base Giuridica</h4>
    <p>Il trattamento è basato sul consenso esplicito dell'utente (Art. 6.1.a GDPR).</p>
    
    <h4 className="font-semibold mt-4">5. Conservazione dei Dati</h4>
    <ul className="list-disc pl-5">
      <li>Dati del profilo: fino alla cancellazione dell'account</li>
      <li>Storico posizioni: massimo 24 ore</li>
      <li>Messaggi: fino alla cancellazione</li>
    </ul>
    
    <h4 className="font-semibold mt-4">6. Diritti dell'Interessato</h4>
    <p>Hai diritto a:</p>
    <ul className="list-disc pl-5">
      <li>Accedere ai tuoi dati personali</li>
      <li>Rettificare dati inesatti</li>
      <li>Cancellare i tuoi dati ("diritto all'oblio")</li>
      <li>Limitare il trattamento</li>
      <li>Portabilità dei dati</li>
      <li>Opporti al trattamento</li>
    </ul>
    
    <h4 className="font-semibold mt-4">7. Contatti</h4>
    <p>Per esercitare i tuoi diritti: privacy@ade-app.com</p>
  </div>
);

// Location consent content
const LocationContent = () => (
  <div className="prose prose-sm max-w-none text-gray-600">
    <h3 className="text-lg font-semibold text-gray-900">Consenso alla Geolocalizzazione</h3>
    
    <h4 className="font-semibold mt-4">Perché raccogliamo la tua posizione</h4>
    <p>ADE utilizza la tua posizione GPS per:</p>
    <ul className="list-disc pl-5">
      <li>Mostrarti utenti nelle tue vicinanze</li>
      <li>Permettere ad altri utenti di trovarti (se sei visibile)</li>
      <li>Calcolare le distanze tra utenti</li>
    </ul>
    
    <h4 className="font-semibold mt-4">Come utilizziamo i dati</h4>
    <ul className="list-disc pl-5">
      <li><strong>Posizione attuale:</strong> aggiornata quando usi l'app</li>
      <li><strong>Storico posizioni:</strong> conservato per massimo 24 ore per motivi di sicurezza</li>
      <li><strong>Visibilità:</strong> puoi disattivare il GPS in qualsiasi momento</li>
    </ul>
    
    <h4 className="font-semibold mt-4">ADE Island</h4>
    <p>Se non vuoi condividere la tua posizione reale, puoi posizionarti su "ADE Island", una località virtuale dove incontrare altri utenti.</p>
    
    <h4 className="font-semibold mt-4">Controllo sui tuoi dati</h4>
    <ul className="list-disc pl-5">
      <li>Puoi attivare/disattivare il GPS in qualsiasi momento</li>
      <li>Lo storico posizioni viene automaticamente eliminato dopo 24 ore</li>
      <li>Puoi richiedere la cancellazione immediata di tutti i dati di posizione</li>
    </ul>
  </div>
);

export default function Register() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [step, setStep] = useState(1); // 1: form data, 2: consents
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [country, setCountry] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  
  // Privacy consents
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [locationConsent, setLocationConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  
  // Dialog states
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  
  const canProceedToConsents = name && email && password && password.length >= 6 && password === confirmPassword && dateOfBirth;
  const canRegister = termsAccepted && privacyAccepted && locationConsent;
  
  const checkAge = () => {
    if (!dateOfBirth) return true;
    const dob = new Date(dateOfBirth);
    const today = new Date();
    const age = Math.floor((today - dob) / (365.25 * 24 * 60 * 60 * 1000));
    return age >= 16;
  };
  
  const handleNextStep = () => {
    if (!name || !email || !password) {
      toast.error(t("regFillRequired"));
      return;
    }
    if (password.length < 6) {
      toast.error(t("regPwdMin"));
      return;
    }
    if (username && !/^[a-zA-Z0-9_]{3,20}$/.test(username)) {
      toast.error(t("regUsernameRule"));
      return;
    }
    if (!dateOfBirth) {
      toast.error(t("regEnterDob"));
      return;
    }
    if (!checkAge()) {
      toast.error(t("regMinAge"));
      return;
    }
    setStep(2);
  };
  
  const handleRegister = async () => {
    if (!canRegister) {
      toast.error(t("regAcceptConsents"));
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/register', { 
        name, 
        email, 
        password,
        username: username || null,
        country: country || null,
        date_of_birth: dateOfBirth || null,
        privacy_consents: {
          terms_accepted: termsAccepted,
          privacy_accepted: privacyAccepted,
          location_consent: locationConsent,
          marketing_consent: marketingConsent
        }
      });
      localStorage.setItem('token', response.data.token);
      if (response.data.user?.user_id) {
        syncKeysOnLogin(response.data.user.user_id, password).catch(() => {});
      }
      toast.success(t("regCreated"));
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/verify-email', { 
        replace: true, 
        state: { 
          user: response.data.user,
          email: email,
          otpPreview: response.data.otp_preview,
          emailSent: response.data.email_sent
        } 
      });
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.detail || t("regError"));
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleRegister = () => {
    const redirectUrl = window.location.origin + '/app/nearby';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const [appleLoading, setAppleLoading] = useState(false);
  const handleAppleRegister = async () => {
    setAppleLoading(true);
    try {
      const data = await appleNativeLogin(api);
      if (data.user.onboarding_completed) {
        navigate('/app/home', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (error) {
      if (error?.response) {
        toast.error(error.response?.data?.detail || "Accesso con Apple non riuscito");
      }
      // native sheet cancelled / no token → silent
    } finally {
      setAppleLoading(false);
    }
  };
  
  // Welcome screen
  if (!showForm) {
    return (
      <div className="min-h-screen relative overflow-hidden" data-testid="register-welcome">
        {/* Premium elite dark background */}
        <div className="absolute inset-0 bg-black">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0510] via-[#0d0815] to-black" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-radial from-purple-900/30 via-purple-900/10 to-transparent opacity-60" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-[100px] animate-pulse-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-600/10 rounded-full blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-purple-600/8 rounded-full blur-[60px] animate-pulse-slow" style={{ animationDelay: '2s' }} />
          <div className="absolute inset-0 opacity-[0.015]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
          }} />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-40 bg-gradient-to-b from-orange-500/50 via-pink-500/20 to-transparent" />
          <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-orange-500/5 to-transparent" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-pink-500/5 to-transparent" />
        </div>
        
        <button
          onClick={() => navigate('/')}
          className="absolute top-6 left-6 z-20 p-2 rounded-full bg-white/5 backdrop-blur-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <button
          onClick={() => setShowForm(true)}
          className="absolute top-6 right-6 z-20 text-white/30 text-sm hover:text-white/60 transition-colors"
        >
          Skip →
        </button>
        
        <div className="relative z-10 min-h-screen flex flex-col">
          <div className="flex-1 flex flex-col items-center justify-center px-8">
            <div className="relative mb-8">
              <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-orange-500/40 via-pink-500/40 to-purple-500/40 scale-150" />
              <div className="relative w-44 h-28 mx-auto flex items-center justify-center">
                <img 
                  src="/images/ade-logo-extracted.png" 
                  alt="ADE" 
                  className="max-w-full max-h-full object-contain drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 0 30px rgba(255, 100, 150, 0.5))' }}
                />
              </div>
            </div>
            
            <h1 className="text-5xl sm:text-6xl font-extralight text-white mt-6 mb-6 tracking-tight">
              Here, Now.
            </h1>
            
            <p className="text-white/40 text-lg text-center max-w-sm leading-relaxed font-light mb-8">
              Connect with interesting people<br />around you in real time.
            </p>
            
            <div className="flex gap-4 mt-4">
              <div className="flex items-center gap-2 bg-white/[0.03] backdrop-blur-sm rounded-full px-4 py-2 border border-white/[0.08]">
                <Shield className="w-4 h-4 text-orange-400" />
                <span className="text-white/50 text-xs font-medium">Verified</span>
              </div>
              <div className="flex items-center gap-2 bg-white/[0.03] backdrop-blur-sm rounded-full px-4 py-2 border border-white/[0.08]">
                <Zap className="w-4 h-4 text-pink-400" />
                <span className="text-white/50 text-xs font-medium">Real-time</span>
              </div>
            </div>
          </div>
          
          <div className="px-6 pb-10">
            <Button
              onClick={() => setShowForm(true)}
              className="w-full h-14 rounded-2xl bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white text-lg font-semibold hover:opacity-90 shadow-xl shadow-pink-500/25 transition-all hover:scale-[1.02] active:scale-[0.98]"
              data-testid="get-started-btn"
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <p className="text-center mt-4 text-white/30 text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-pink-400/80 hover:text-pink-400 font-medium">
                Sign In
              </Link>
            </p>
          </div>
        </div>
        
        <style>{`
          @keyframes pulse-slow {
            0%, 100% { opacity: 0.6; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.05); }
          }
          .animate-pulse-slow {
            animation: pulse-slow 6s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }
  
  // Step 1: User data form
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-orange-50/30 to-pink-50/30 flex flex-col" data-testid="register-form">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={() => setShowForm(false)}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
          <span className="text-sm text-gray-400">{t("step1of2")}</span>
          <div className="w-9" />
        </div>
        
        <div className="flex-1 px-6 py-4 overflow-y-auto">
          <div className="max-w-sm mx-auto">
            <div className="flex flex-col items-center mb-8">
              <div className="w-24 h-14 flex items-center justify-center mb-2">
                <img 
                  src="/images/ade-logo-extracted.png" 
                  alt="ADE" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <span className="text-gray-400 text-xs tracking-[0.2em]">{t("createAccountCaps")}</span>
            </div>
            
            <Button
              data-testid="google-register-btn"
              variant="outline"
              className="w-full h-14 rounded-2xl border-gray-200 mb-4 font-medium hover:bg-gray-50 text-base"
              onClick={handleGoogleRegister}
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t("continueWithGoogle")}
            </Button>
            
            {/* Apple Register (native iOS only) */}
            {isNativeIOS() && (
              <Button
                data-testid="apple-register-btn"
                className="w-full h-14 rounded-2xl bg-black text-white hover:bg-gray-900 mb-4 font-medium text-base"
                onClick={handleAppleRegister}
                disabled={appleLoading}
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                {t("continueWithApple")}
              </Button>
            )}
            
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-xs text-gray-400 font-medium">{t("orLabel")}</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>
            
            <div className="space-y-3">
              <Input
                type="text"
                placeholder={t("phName")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-13 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white text-base px-4"
                data-testid="name-input"
                required
              />
              
              <Input
                type="text"
                placeholder={t("phUsername")}
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                className="h-13 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white text-base px-4"
                data-testid="username-input"
                maxLength={20}
              />
              <p className="text-[10px] text-gray-400 px-1 -mt-1">{t("usernameHint")}</p>
              
              <div className="relative">
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full h-13 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white text-base px-4 appearance-none cursor-pointer text-gray-700"
                  data-testid="country-select"
                >
                  <option value="">{t("selectCountry")}</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                  ))}
                </select>
                <Globe className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              
              <Input
                type="email"
                name="email"
                autoComplete="email"
                placeholder={t("phEmail")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-13 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white text-base px-4"
                data-testid="email-input"
                required
              />
              
              <Input
                type="password"
                name="new-password"
                autoComplete="new-password"
                placeholder={t("phPassword")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-13 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white text-base px-4"
                data-testid="password-input"
                required
              />
              <p className="text-[10px] text-gray-400 px-1">{t("pwdMinHint")}</p>

              <Input
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                placeholder={t("confirmPasswordPh")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`h-13 rounded-xl bg-gray-50/50 focus:bg-white text-base px-4 ${confirmPassword && confirmPassword !== password ? "border-red-300" : "border-gray-200"}`}
                data-testid="confirm-password-input"
                required
              />
              {confirmPassword && confirmPassword !== password && (
                <p className="text-[11px] text-red-500 px-1" data-testid="register-pwd-mismatch">{t("pwdMismatch")}</p>
              )}
              
              <div>
                <label className="text-xs text-gray-500 px-1 mb-1 block">{t("dobLabel")}</label>
                <Input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split('T')[0]}
                  className="h-13 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white text-base px-4"
                  data-testid="dob-input"
                  required
                />
                <p className="text-[10px] text-gray-400 px-1">{t("minAgeHint")}</p>
              </div>
              
              <Button
                onClick={handleNextStep}
                disabled={!canProceedToConsents}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white text-lg font-semibold hover:opacity-90 shadow-xl shadow-pink-500/25 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-step-btn"
              >
                Continua
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            <p className="text-center mt-6 text-gray-500 text-sm">
              {t("alreadyAccount")}{" "}
              <Link to="/login" className="text-pink-500 hover:text-pink-600 font-semibold">
                Accedi
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Step 2: Privacy consents
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-orange-50/30 to-pink-50/30 flex flex-col" data-testid="register-consents">
      <div className="flex items-center justify-between p-4">
        <button 
          onClick={() => setStep(1)}
          className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
        <span className="text-sm text-gray-400">Passaggio 2 di 2</span>
        <div className="w-9" />
      </div>
      
      <div className="flex-1 px-6 py-4 overflow-y-auto">
        <div className="max-w-sm mx-auto">
          <div className="flex flex-col items-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Privacy & Consensi</h2>
            <p className="text-gray-500 text-sm text-center mt-2">
              Per utilizzare ADE devi accettare i seguenti consensi
            </p>
          </div>
          
          {/* Consent cards */}
          <div className="space-y-3">
            {/* Terms of Service */}
            <div 
              className={`p-4 rounded-xl border-2 transition-colors ${
                termsAccepted ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-1"
                  data-testid="terms-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="terms" className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                    <FileText className="w-4 h-4 text-orange-500" />
                    Termini e Condizioni *
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Accetto i termini di utilizzo del servizio ADE
                  </p>
                  <button
                    onClick={() => setShowTerms(true)}
                    className="text-xs text-pink-500 hover:text-pink-600 mt-1 underline"
                  >
                    Leggi i Termini e Condizioni
                  </button>
                </div>
                {termsAccepted && <Check className="w-5 h-5 text-green-500" />}
              </div>
            </div>
            
            {/* Privacy Policy */}
            <div 
              className={`p-4 rounded-xl border-2 transition-colors ${
                privacyAccepted ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy"
                  checked={privacyAccepted}
                  onCheckedChange={setPrivacyAccepted}
                  className="mt-1"
                  data-testid="privacy-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="privacy" className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-500" />
                    Privacy Policy *
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Acconsento al trattamento dei miei dati personali secondo GDPR
                  </p>
                  <button
                    onClick={() => setShowPrivacy(true)}
                    className="text-xs text-pink-500 hover:text-pink-600 mt-1 underline"
                  >
                    Leggi l'Informativa Privacy
                  </button>
                </div>
                {privacyAccepted && <Check className="w-5 h-5 text-green-500" />}
              </div>
            </div>
            
            {/* Location consent */}
            <div 
              className={`p-4 rounded-xl border-2 transition-colors ${
                locationConsent ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="location"
                  checked={locationConsent}
                  onCheckedChange={setLocationConsent}
                  className="mt-1"
                  data-testid="location-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="location" className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    Geolocalizzazione *
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Acconsento alla raccolta e utilizzo della mia posizione GPS
                  </p>
                  <button
                    onClick={() => setShowLocation(true)}
                    className="text-xs text-pink-500 hover:text-pink-600 mt-1 underline"
                  >
                    Maggiori informazioni
                  </button>
                </div>
                {locationConsent && <Check className="w-5 h-5 text-green-500" />}
              </div>
            </div>
            
            {/* Marketing consent (optional) */}
            <div 
              className={`p-4 rounded-xl border-2 transition-colors ${
                marketingConsent ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="marketing"
                  checked={marketingConsent}
                  onCheckedChange={setMarketingConsent}
                  className="mt-1"
                  data-testid="marketing-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="marketing" className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-500" />
                    Comunicazioni Marketing
                    <span className="text-xs text-gray-400 font-normal">(opzionale)</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Voglio ricevere novità, offerte e aggiornamenti su ADE
                  </p>
                </div>
                {marketingConsent && <Check className="w-5 h-5 text-green-500" />}
              </div>
            </div>
          </div>
          
          <p className="text-xs text-gray-400 text-center mt-4">
            * Consenso obbligatorio per utilizzare ADE
          </p>
          
          <Button
            onClick={handleRegister}
            disabled={!canRegister || loading}
            className="w-full h-14 rounded-2xl bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white text-lg font-semibold hover:opacity-90 shadow-xl shadow-pink-500/25 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            data-testid="create-account-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creazione...
              </span>
            ) : (
              <>
                Crea Account
                <Check className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Terms Dialog */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Termini e Condizioni</DialogTitle>
          </DialogHeader>
          <TermsContent />
          <Button onClick={() => { setTermsAccepted(true); setShowTerms(false); }} className="w-full mt-4">
            Accetto i Termini
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Privacy Dialog */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Informativa Privacy</DialogTitle>
          </DialogHeader>
          <PrivacyContent />
          <Button onClick={() => { setPrivacyAccepted(true); setShowPrivacy(false); }} className="w-full mt-4">
            Accetto la Privacy Policy
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Location Dialog */}
      <Dialog open={showLocation} onOpenChange={setShowLocation}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Consenso Geolocalizzazione</DialogTitle>
          </DialogHeader>
          <LocationContent />
          <Button onClick={() => { setLocationConsent(true); setShowLocation(false); }} className="w-full mt-4">
            Acconsento alla Geolocalizzazione
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
